import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageSquare, Loader2 } from "lucide-react";
import { ChatMessage } from "./ChatMessage";
import { ChatHeader } from "./ChatHeader";
import { ChatInput } from "./ChatInput";
import { useChatMessages } from "@/hooks/chat/useChatMessages";

export const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { 
    messages, 
    isLoading, 
    isFullScreen, 
    sendMessage, 
    clearChat, 
    toggleFullScreen, 
    handleEntityClick 
  } = useChatMessages();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Scroll to bottom of messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Focus input when popover opens
  useEffect(() => {
    if ((isOpen || isFullScreen) && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, isFullScreen]);

  const handleChatClick = (e: React.MouseEvent) => {
    const navigated = handleEntityClick(e);
    if (navigated) {
      setIsOpen(false); // Close chat after navigation
      if (isFullScreen) {
        toggleFullScreen(); // Close fullscreen mode after navigation
      }
    }
  };

  // Render chat content
  const renderChatContent = () => (
    <div className="flex flex-col h-[500px] max-h-[80vh]">
      <ChatHeader 
        onClearChat={clearChat} 
        onToggleFullScreen={toggleFullScreen} 
        isFullScreen={isFullScreen} 
      />
      <ScrollArea className="flex-1 p-3 overflow-y-auto">
        <div className="space-y-4" onClick={handleChatClick}>
          {messages.map((message) => (
            <ChatMessage 
              key={message.id} 
              message={message} 
              onEntityClick={handleEntityClick}
            />
          ))}
          {isLoading && (
            <div className="flex justify-center py-2">
              <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>
      <ChatInput 
        onSendMessage={sendMessage}
        isLoading={isLoading}
        inputRef={inputRef}
      />
    </div>
  );

  // If in fullscreen mode, render dialog instead of popover
  if (isFullScreen) {
    return (
      <Dialog open={isFullScreen} onOpenChange={(open) => !open && toggleFullScreen()}>
        <DialogContent className="sm:max-w-[800px] p-0">
          {renderChatContent()}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            style={{ backgroundColor: 'rgb(37 99 235 / var(--tw-bg-opacity, 1))' }}
            className="h-12 w-12 rounded-full shadow-xl backdrop-blur-lg transition-colors p-0 flex items-center justify-center border-2 border-white/70 hover:brightness-110"
            size="icon"
            aria-label="Open chat"
          >
            <MessageSquare className="h-7 w-7 text-white" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-80 sm:w-96 p-0 rounded-2xl shadow-2xl border border-digger-blue/20 bg-white/60 backdrop-blur-xl"
          side="top"
          align="end"
        >
          {renderChatContent()}
        </PopoverContent>
      </Popover>
    </div>
  );
};
